import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Zap, 
  FileText, 
  User, 
  Target, 
  CheckCircle, 
  Building2, 
  Bookmark, 
  ExternalLink,
  MapPin,
  DollarSign,
  Calendar
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { SelectedJob, useJobContext } from "@/contexts/JobContext";
import { formatDistanceToNow } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";

interface JobDetailModalProps {
  job: SelectedJob | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSmartApply?: (job: SelectedJob) => void;
  onSave?: (job: SelectedJob) => void;
}

export function JobDetailModal({ job, open, onOpenChange, onSmartApply, onSave }: JobDetailModalProps) {
  const navigate = useNavigate();
  const { setSelectedJob } = useJobContext();

  if (!job) return null;

  const handleAction = (action: string) => {
    // Store job in context for feature pages to access
    setSelectedJob(job);
    onOpenChange(false);
    
    switch (action) {
      case 'cover-letter':
        navigate('/app/letters');
        break;
      case 'resume':
        navigate('/app/resume');
        break;
      case 'job-analyzer':
        navigate('/app/job-analyzer');
        break;
      case 'ats-checker':
        navigate('/app/ats-score-checker');
        break;
      case 'company-insights':
        navigate('/app/company-insights');
        break;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-2xl mb-2">{job.title}</DialogTitle>
              <DialogDescription className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-base">
                  {job.logo ? (
                    <img src={job.logo} alt={job.company} className="w-5 h-5 rounded object-contain" />
                  ) : (
                    <Building2 className="w-4 h-4" />
                  )}
                  <span className="font-medium text-foreground">{job.company}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-sm">
                  {job.location && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {job.location}
                    </div>
                  )}
                  {job.salary && (
                    <div className="flex items-center gap-1">
                      <DollarSign className="w-4 h-4" />
                      {job.salary}
                    </div>
                  )}
                  {job.posted_at && (
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDistanceToNow(new Date(job.posted_at), { addSuffix: true })}
                    </div>
                  )}
                </div>
              </DialogDescription>
            </div>
            <div className="flex flex-col gap-2">
              {job.apply_type === 'EASY_APPLY' && (
                <Badge className="bg-green-500/10 text-green-700 border-green-500/30">
                  Easy Apply
                </Badge>
              )}
              <Badge variant="secondary">{job.source}</Badge>
            </div>
          </div>
        </DialogHeader>

        <Separator />

        <ScrollArea className="max-h-[40vh] px-6">
          <div className="py-4 space-y-4">
            <div>
              <h3 className="font-semibold mb-2">Job Description</h3>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap">
                {job.description || "No description available."}
              </div>
            </div>
          </div>
        </ScrollArea>

        <Separator />

        <div className="p-6 pt-4 space-y-4">
          <div>
            <h3 className="font-semibold mb-3">Use this job with...</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => {
                  if (onSmartApply) {
                    onSmartApply(job);
                    onOpenChange(false);
                  }
                }}
              >
                <Zap className="h-4 w-4 text-primary" />
                Smart Apply
              </Button>
              
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => handleAction('cover-letter')}
              >
                <FileText className="h-4 w-4" />
                Cover Letter
              </Button>
              
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => handleAction('resume')}
              >
                <User className="h-4 w-4" />
                Resume
              </Button>
              
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => handleAction('job-analyzer')}
              >
                <Target className="h-4 w-4" />
                Job Analyzer
              </Button>
              
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => handleAction('ats-checker')}
              >
                <CheckCircle className="h-4 w-4" />
                ATS Checker
              </Button>
              
              <Button
                variant="outline"
                className="justify-start gap-2"
                onClick={() => handleAction('company-insights')}
              >
                <Building2 className="h-4 w-4" />
                Company Insights
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            {onSave && (
              <Button
                variant="secondary"
                className="flex-1 gap-2"
                onClick={() => {
                  onSave(job);
                  onOpenChange(false);
                }}
              >
                <Bookmark className="h-4 w-4" />
                Save Job
              </Button>
            )}
            <Button
              variant="outline"
              className="flex-1 gap-2"
              onClick={() => window.open(job.url, '_blank')}
            >
              <ExternalLink className="h-4 w-4" />
              View Original
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
